import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useStytch, useStytchSession } from "@stytch/react";
import { useCurrentUser, User } from "@/lib/api/user.ts";
import { AbilityProvider } from "@/lib/casl/ability-context";
import { isImpersonatedSession } from "@/lib/impersonation";
import { Session } from "@stytch/vanilla-js";
import { useQueryClient } from "@tanstack/react-query";
import OneSignal from "react-onesignal";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticating: boolean;
  revokeSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>({
  user: null,
  session: null,
  isAuthenticating: true,
  revokeSession: () => Promise.resolve(),
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useStytchSession();
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading } = useCurrentUser();
  const stytch = useStytch();

  const [initialized, setInitialized] = useState(false);

  const revokeSession = async () => {
    await stytch.session.revoke();
    queryClient.removeQueries({
      queryKey: ["current-user"],
    });
    // Gate decisions depend on this — don't leave a prior session's review cached.
    queryClient.removeQueries({
      queryKey: ["mentor-profile"],
    });
    if (!initialized) return;
    await OneSignal.logout().catch((error) => {
      console.warn("OneSignal logout failed; continuing sign-out", error);
    });
  };

  useEffect(() => {
    if (initialized || isLoading || !currentUser) {
      return;
    }

    // Never bind push identity during impersonation
    if (isImpersonatedSession(session)) {
      return;
    }

    OneSignal.init({
      appId: import.meta.env.VITE_ONESIGNAL_APP_ID as string,
      allowLocalhostAsSecureOrigin: true,
    }).then(() => {
      setInitialized(true);
      OneSignal.login(currentUser.id).then(() => {
        if (currentUser.phone) OneSignal.User.addSms(currentUser.phone);
        if (currentUser.email) OneSignal.User.addEmail(currentUser.email);
      });
    });
  }, [initialized, isLoading, currentUser, session]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticating: isLoading,
        user: isLoading || !currentUser ? null : currentUser,
        session,
        revokeSession,
      }}
    >
      <AbilityProvider rules={currentUser?.abilities}>
        {children}
      </AbilityProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
